import { TradingPlatforms } from '@monorepo/client-types';
import { SystemIconNames } from '@monorepo/nova-icons';
import { TradingSystem } from '@monorepo/types';

export type TradingPlatformData = {
  platform: TradingPlatforms;
  link: string;
};

export const PlatformBtnText: { [key in TradingPlatforms]: string } = {
  [TradingPlatforms.Web]: 'Web',
  [TradingPlatforms.Mac]: 'Mac',
  [TradingPlatforms.Android]: 'Android',
  [TradingPlatforms.iOS]: 'iOS',
  [TradingPlatforms.Windows]: 'Windows',
  [TradingPlatforms.Linux]: 'Linux',
};

export const PlatformIconMap: { [key in TradingPlatforms]: SystemIconNames } = {
  [TradingPlatforms.Windows]: 'WindowsLogo',
  [TradingPlatforms.Mac]: 'AppleLogo',
  [TradingPlatforms.Linux]: 'LinuxLogo',
  [TradingPlatforms.Web]: 'Globe',
  [TradingPlatforms.Android]: 'AndroidLogo',
  [TradingPlatforms.iOS]: 'AppleLogo',
};

export const TradingPlatformServers: Record<TradingSystem, string> = {
  [TradingSystem.MetaTraderFive]: 'FivePercentOnline-Real',
  [TradingSystem.MetaTraderXP]: 'FivePercentOnline-Experience ',
  [TradingSystem.MatchTrader]: '',
  [TradingSystem.TraderEvolution]: '',
  [TradingSystem.MetaTraderContest]: 'FivePercentOnline-Experience ',
  [TradingSystem.CTrader]: '',
  [TradingSystem.BlackArrowFutures]: '',
};

const MT5_DOWNLOAD_LINKS: TradingPlatformData[] = [
  {
    platform: TradingPlatforms.Windows,
    link: 'https://download.terminal.free/cdn/web/five.percent.online/mt5/fivepercentonline5setup.exe',
  },
  {
    platform: TradingPlatforms.Mac,
    link: 'https://download.mql5.com/cdn/web/metaquotes.software.corp/mt5/MetaTrader5.pkg.zip',
  },
  {
    platform: TradingPlatforms.Linux,
    link: 'https://www.metatrader5.com/en/terminal/help/start_advanced/install_linux/',
  },
  {
    platform: TradingPlatforms.iOS,
    link: 'https://download.terminal.free/cdn/mobile/mt5/ios?server=FivePercentOnline-Experience,FivePercentOnline-Real',
  },
  {
    platform: TradingPlatforms.Android,
    link: 'https://download.terminal.free/cdn/mobile/mt5/android?server=FivePercentOnline-Experience,FivePercentOnline-Real',
  },
];

export const getTradingPlatform = (
  tradingSystem: TradingSystem,
  isProd = true,
) => {
  const tradingSystemPlatformMap: {
    [key: string]: TradingPlatformData[];
  } = {
    [TradingSystem.MatchTrader]: [
      { platform: TradingPlatforms.Web, link: 'https://mtr.the5ers.com/' },
    ],
    [TradingSystem.MetaTraderFive]: [
      ...MT5_DOWNLOAD_LINKS,
      {
        platform: TradingPlatforms.Web,
        link: 'https://mt5.the5ers.com/terminal',
      },
    ],
    [TradingSystem.MetaTraderXP]: [
      ...MT5_DOWNLOAD_LINKS,
      {
        platform: TradingPlatforms.Web,
        link: 'https://mt5-2.the5ers.com/terminal',
      },
    ],
    [TradingSystem.MetaTraderContest]: [
      ...MT5_DOWNLOAD_LINKS,
      {
        platform: TradingPlatforms.Web,
        link: 'https://mt5-2.the5ers.com/terminal',
      },
    ],
    [TradingSystem.TraderEvolution]: [
      {
        platform: TradingPlatforms.Windows,
        link: isProd
          ? 'https://update.traderevolution.com/Trade_The_Pool/Trade%20The%20Pool.exe'
          : 'https://update.traderevolution.com/Trade_The_Pool_UAT/Trade%20The%20Pool%20UAT.exe',
      },
      {
        platform: TradingPlatforms.Android,
        link: 'https://play.google.com/store/apps/details?id=com.traderevolution.tradethepool',
      },
      {
        platform: TradingPlatforms.iOS,
        link: 'https://apps.apple.com/us/app/trade-the-pool/id1628363224',
      },
      {
        platform: TradingPlatforms.Web,
        link: 'https://web.tradethepool.com/',
      },
    ],
    [TradingSystem.CTrader]: [
      {
        platform: TradingPlatforms.Windows,
        link: 'https://getctrader.com/the5ers/ctrader-the5ers-setup.exe',
      },
      {
        platform: TradingPlatforms.Android,
        link: 'https://play.google.com/store/apps/details?id=com.spotware.ct&pli=1',
      },
      {
        platform: TradingPlatforms.iOS,
        link: 'https://apps.apple.com/cy/app/ctrader-forex-stock-trading/id767428811',
      },
      {
        platform: TradingPlatforms.Mac,
        link: 'https://getctradermac.com/the5ers/ctrader-the5ers-setup.dmg',
      },
      {
        platform: TradingPlatforms.Web,
        link: 'https://app.the5ers.com/',
      },
    ],
    [TradingSystem.BlackArrowFutures]: [
      {
        platform: TradingPlatforms.Windows,
        link: 'https://downloadserver-cdn.nelogica.com.br/produtos/bin/profitchart/BlackArrow_ENUS.exe?_gl=1*18bywst*_gcl_au*MTI3OTQ4ODc1MC4xNzY3MTAwNzM5',
      },
      {
        platform: TradingPlatforms.Mac,
        link: 'https://downloadserver-cdn.nelogica.com.br/content/mac/blackarrow/stable/arm64/blackarrow.dmg?_gl=1*52uaun*_gcl_au*MTI3OTQ4ODc1MC4xNzY3MTAwNzM5',
      },
      {
        platform: TradingPlatforms.iOS,
        link: 'https://apps.apple.com/br/app/blackarrow/id1475643637',
      },
      {
        platform: TradingPlatforms.Android,
        link: 'https://play.google.com/store/apps/details?id=profitchartandroid.nelogica.com.profitchartandroid.blackArrowNelogica',
      },
      {
        platform: TradingPlatforms.Web,
        link: 'https://web.blackarrowtrading.com/?_gl=1*52uaun*_gcl_au*MTI3OTQ4ODc1MC4xNzY3MTAwNzM5',
      },
    ],
  };

  return tradingSystemPlatformMap[tradingSystem];
};
